// =====================================================================
// Le micro
// =====================================================================
// Enregistrer un ancien qui récite la lignée. Trois choses à faire
// correctement, et aucune n'est évidente :
//
// LE FORMAT. Safari n'enregistre pas en WebM : s'en tenir à
// `audio/webm;codecs=opus` perd tous les iPhone, sans message d'erreur —
// `MediaRecorder` se contente de refuser le type. On essaie donc les
// formats dans l'ordre et on garde le premier accepté.
//
// LE DÉBIT. Par défaut, un navigateur enregistre de la musique : 128 kbps,
// soit près de dix méga-octets pour dix minutes. Sur une connexion de
// village, ce fichier ne partira jamais. À 32 kbps, la parole reste
// parfaitement claire et dix minutes tiennent en 2,4 Mo.
//
// LE MICRO QU'ON REND. Sans `track.stop()`, le voyant du téléphone reste
// allumé après l'enregistrement. L'utilisateur croit — à juste titre —
// qu'on l'écoute encore.
// =====================================================================

const FORMATS = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

export interface Prise {
  son: Blob;
  duree_s: number;
}

export function microDisponible(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined'
  );
}

function formatRetenu(): string | undefined {
  return FORMATS.find((f) => MediaRecorder.isTypeSupported(f));
}

export class Enregistreur {
  private flux: MediaStream | null = null;
  private magneto: MediaRecorder | null = null;
  private morceaux: Blob[] = [];
  private debut = 0;

  /** Demande le micro et commence. L'autorisation est demandée ici, au
   *  moment où l'utilisateur appuie — jamais à l'ouverture de la page. */
  async demarrer(): Promise<void> {
    this.flux = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    const type = formatRetenu();
    this.magneto = new MediaRecorder(this.flux, {
      ...(type ? { mimeType: type } : {}),
      audioBitsPerSecond: 32000,
    });

    this.morceaux = [];
    this.magneto.ondataavailable = (e) => {
      if (e.data.size > 0) this.morceaux.push(e.data);
    };

    this.debut = Date.now();
    // Un morceau par seconde : si l'application est tuée en cours
    // d'enregistrement, on perd une seconde, pas dix minutes.
    this.magneto.start(1000);
  }

  get enCours(): boolean {
    return this.magneto?.state === 'recording';
  }

  /** Arrête, rend le micro, et renvoie la prise. */
  arreter(): Promise<Prise> {
    return new Promise((resolve, rejeter) => {
      const m = this.magneto;
      if (!m) {
        rejeter(new Error('Aucun enregistrement en cours'));
        return;
      }

      m.onstop = () => {
        const son = new Blob(this.morceaux, {
          type: m.mimeType || 'audio/webm',
        });
        this.rendreLeMicro();
        resolve({
          son,
          duree_s: Math.max(1, Math.round((Date.now() - this.debut) / 1000)),
        });
      };

      m.stop();
    });
  }

  /** Abandonne sans rien produire — et rend le micro quand même. */
  annuler(): void {
    try {
      this.magneto?.stop();
    } catch {
      /* déjà arrêté */
    }
    this.morceaux = [];
    this.rendreLeMicro();
  }

  private rendreLeMicro(): void {
    for (const piste of this.flux?.getTracks() ?? []) piste.stop();
    this.flux = null;
    this.magneto = null;
  }
}
